import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['form', 'input'];
  static values = { url: String, forceVisit: Boolean };

  connect() {
    this.inputTargets.forEach((input) => {
      input.addEventListener('change', this.fetchForm.bind(this));
    });
  }

  fetchForm(event) {
    // Fields that depend on the one that changed (data-form-resets, space-separated names)
    // are left out of the refresh, so the server re-renders them blank instead of keeping
    // a choice that belonged to the previous value.
    this.resets = event?.target?.dataset?.formResets?.split(' ') || [];

    // The field that just changed is about to have its whole form section
    // replaced by the response below. Turbo's own focus restoration
    // (withPreservedFocus in @hotwired/turbo) otherwise re-focuses whatever
    // ends up at the same id in the freshly-rendered markup once it lands —
    // for a tom-select-enhanced field (searchable-select controller) that's
    // its "-ts-control" focus node, which tom-select assigns the same
    // deterministic id on every re-init. Focusing it re-triggers tom-select's
    // openOnFocus (its default, never overridden), popping the dropdown back
    // open right after the user picked an option. Blurring first leaves
    // nothing for Turbo to try to restore.
    document.activeElement?.blur();

    if (this.forceVisitValue) {
      this.visitFormUrl();
    } else {
      this.fetchFromAsync();
    }
  }

  visitFormUrl() {
    Turbo.visit(this.urlWithQueryString());
  }

  async fetchFromAsync() {
    const response = await fetch(this.urlWithQueryString(), {
      headers: {
        Accept: 'text/vnd.turbo-stream.html',
      },
    });
    const contentType = response.headers.get('Content-Type') || '';
    if (!contentType.includes('turbo-stream')) {
      return;
    }
    const html = await response.text();
    Turbo.renderStreamMessage(html);
  }

  urlWithQueryString() {
    return `${this.url()}?${this.queryString()}`;
  }

  url() {
    if (this.urlValue) {
      return this.urlValue;
    } else {
      return window.location.href.split('?')[0];
    }
  }

  queryString() {
    const form = new FormData(this.formTarget);
    const params = new URLSearchParams(window.location.search);
    for (const [name, value] of form.entries()) {
      if (name !== 'utf8' && !(value instanceof File)) {
        params.delete(name);
        if (!this.resets?.includes(name)) params.append(name, value);
      }
    }

    return params.toString();
  }
}
